import React, { useState } from "react";
import { Box, Button, Divider, IconButton, TextField, Typography, CircularProgress, Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";

import logo from "../../assets/logo.svg";
import styles from "./Footer.module.scss";
import arrow from "../../assets/Arrow_B.svg";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useRecaptchaAction from "../../utils/useRecaptchaAction";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [privacyError, setPrivacyError] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const { hash } = useLocation();
  const { getRecaptchaPayload } = useRecaptchaAction();
  useEffect(() => {
    if (!hash) return;

    const id = hash.slice(1); // usuwa '#'
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;

      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  // const onSubscribe = async (e) => {
  //   e.preventDefault();
  //   if (!email) {
  //     toast.error("Brak wpisanego maila.");
  //     setStatus("idle");
  //     return;
  //   }

  //   setStatus("loading");

  //   try {
  //     const response = await fetch("https://newslettersubscribe-337224913158.europe-west1.run.app", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ email }),
  //     });

  //     if (response.ok) {
  //       setStatus("success");
  //       setEmail("");
  //     } else {
  //       setStatus("idle");
  //       toast.error("Wystąpił błąd. Spróbuj ponownie.");
  //     }
  //   } catch (error) {
  //     console.error("Newsletter error:", error);
  //     setStatus("idle");
  //     toast.error("Wystąpił błąd. Spróbuj ponownie.");
  //   }

  // };
  const onSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(t("footer.missingEmail"));
      setStatus("idle");
      return;
    }
    if (!privacyConsent) {
      setPrivacyError(t("footer.privacyError"));
      setStatus("idle");
      return;
    }

    setStatus("loading");

    try {
      const recaptcha = await getRecaptchaPayload("newsletter_subscribe_footer");

      const response = await fetch(
        "https://newslettersubscribe-337224913158.europe-west1.run.app",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            ...recaptcha,
          }),
        }
      );

      if (response.ok) {
        setStatus("success");
        setEmail("");
        setPrivacyConsent(false);
        setPrivacyError("");
      } else {
        setStatus("idle");
        toast.error(t("footer.genericError"));
      }
    } catch (error) {
      console.error("Newsletter error:", error);
      setStatus("idle");
      toast.error(t("footer.genericError"));
    }
  };
  return (
    <>
      <ToastContainer position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />
      <Divider className={styles.divider} />
      <Box component="footer" className={styles.footer}>

        <div className={styles.inner}>
          <div className={styles.row}>
            <div className={styles.brand}>
              <img src={logo} alt="AlfaGym" className={styles.logo} />
              <Typography component="div" className={styles.contact}>
                ALFA GYM SP. Z O.O. <br />
                ul. Krzysztofa Cedry 6 <br />
                91-129 Łódź<br />
                tel.: +48 786 087 447<br />
                e-mail: kontakt@alfagym.pl<br />
              </Typography>
            </div>

            {/* MENU */}
            <nav className={styles.col} aria-label={t("footer.menu")}>
              <a href="/" className={styles.link}>
                {t("footer.home")}
              </a>
              <a href="/rezerwacja#kalendarz" className={styles.link}>
                {t("common.book")}
              </a>
              <a href="/oferta" className={styles.link}>
                {t("footer.offer")}
              </a>
              <a href="/kontakt" className={styles.link}>
                {t("footer.contact")}
              </a>
            </nav>

            {/* POLITYKI + SOCIAL */}
            <div className={styles.col}>
              <a href="/polityka-prywatnosci" className={styles.link}>
                {t("footer.privacy")}
              </a>
              <a href="/regulamin" className={styles.link}>
                {t("footer.terms")}
              </a>

              <div className={styles.socialRow}>
                <IconButton href="https://www.instagram.com/alfagym_studio/"
                  target="_blank"
                  rel="noopener noreferrer" className={styles.socialIcon} aria-label={t("common.instagram")}>
                  <InstagramIcon />
                </IconButton>
              </div>
            </div>

            {/* NEWSLETTER */}
            <div className={styles.news}>
              <h4 className={styles.title}>{t("footer.stayWithUs")}</h4>

              <form onSubmit={onSubscribe} className={styles.form}>
                <label htmlFor="email" className={styles.label}>{t("footer.email")}</label>
                <TextField
                  id="email"
                  name="email"
                  type="email"
                  variant="standard"
                  placeholder="name@example.com"
                  fullWidth
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  disabled={status === "loading"}
                  className={styles.emailField}
                />
                <div className={styles.checkboxGroup}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={privacyConsent}
                        onChange={(e) => {
                          setPrivacyConsent(e.target.checked);
                          if (privacyError) {
                            setPrivacyError("");
                          }
                        }}
                        disabled={status === "loading"}
                        sx={{
                          color: privacyError ? "#d32f2f" : "white",
                          "&.Mui-checked": { color: "#FF0000" },
                        }}
                      />
                    }
                    label={
                      <span className={styles.checkboxLabelText}>
                        {t("footer.privacyConsentStart")}{" "}
                        <a href="/regulamin" target="_blank" rel="noopener noreferrer">
                          {t("footer.termsConsent")}
                        </a>{" "}
                        {t("footer.and")}{" "}
                        <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer">
                          {t("footer.privacyConsent")}
                        </a>
                      </span>
                    }
                    className={styles.checkboxLabel}
                  />
                  {privacyError ? <FormHelperText error>{privacyError}</FormHelperText> : null}
                </div>
                <Button
                  type="submit"
                  disableRipple
                  className={styles.subscribeBtn}
                  disabled={status === "loading"}
                >
                  {status === "loading" ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <>
                      {t("footer.subscribe")}
                      <img className={styles.arrowIcon} src={arrow} alt="" aria-hidden="true" />
                    </>
                  )}
                </Button>
                {/* Status Messages */}
                {status === "success" && (
                  <Typography variant="caption" sx={{ color: "#4caf50", mt: 1, display: 'block' }}>
                    {t("footer.subscribeSuccess")}
                  </Typography>
                )}

              </form>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
}
